import React, { useState } from "react";
import { toast } from "sonner";
import { Gift, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { http, formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PaymentMethodDialog from "@/components/PaymentMethodDialog";

const SESSIONS = [
    { id: "reiki", name: "Soins énergétiques avec le langage de lumière", price: 80, desc: "60 min d'harmonisation profonde par les soins énergétiques avec le langage de lumière." },
    { id: "magnetisme", name: "Connexion à ses guides", price: 40, desc: "45 min de dialogue avec vos guides." },
    { id: "guidance", name: "Programme accéléré", price: 288, desc: "60 min d'accompagnement intensif." },
];

export default function GiftPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState({
        recipient_name: "",
        recipient_email: "",
        sender_name: user?.name || "",
        message: "",
    });
    const [busy, setBusy] = useState(false);
    const [dialog, setDialog] = useState(null);

    const openPayment = () => {
        if (!user || user === false) {
            toast.info("Connectez-vous pour offrir une séance.");
            navigate("/connexion");
            return;
        }
        if (!selected) return toast.error("Choisissez une séance à offrir.");
        if (!form.recipient_name || !form.recipient_email || !form.sender_name) {
            return toast.error("Merci de renseigner les informations du destinataire et votre nom.");
        }
        const sess = SESSIONS.find((s) => s.id === selected);
        setDialog({
            kind: "gift",
            item_name: `Cadeau : ${sess.name}`,
            amount: sess.price * 100,
            origin_url: window.location.origin,
            gift: {
                session_type_id: selected,
                recipient_name: form.recipient_name,
                recipient_email: form.recipient_email,
                sender_name: form.sender_name,
                message: form.message,
            },
        });
    };

    const stripeCheckout = async () => {
        const { data } = await http.post("/gifts/checkout", {
            session_type_id: selected,
            origin_url: window.location.origin,
            ...form,
        });
        window.location.href = data.checkout_url;
    };

    return (
        <div className="max-w-4xl mx-auto px-6 py-20" data-testid="gift-page">
            <Seo
                title="Offrir un soin · Foundation of Light"
                description="Offrez une séance de soins énergétiques avec le langage de lumière à un proche : bon cadeau numérique avec code personnalisé et message, valable 12 mois."
                path="/cadeau"
            />
            <div className="text-center mb-14 max-w-2xl mx-auto">
                <Gift className="w-10 h-10 text-secondary mx-auto mb-4" />
                <div className="text-sm tracking-[0.25em] uppercase text-primary mb-4 font-medium">Offrir</div>
                <h1 className="font-serif text-5xl sm:text-6xl mb-6">Un cadeau de présence</h1>
                <p className="text-lg text-foreground/70">
                    Offrez une séance de soin énergétique à un proche. Un lien unique lui sera envoyé
                    pour choisir son créneau à son rythme.
                </p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-10">
                {SESSIONS.map((s) => (
                    <button
                        key={s.id}
                        onClick={() => setSelected(s.id)}
                        className={`text-left rounded-3xl border p-6 transition-all ${
                            selected === s.id ? "border-primary bg-accent shadow-lg" : "border-border/60 bg-card hover:border-primary/40"
                        }`}
                        data-testid={`gift-session-${s.id}`}
                    >
                        <h3 className="font-serif text-2xl mb-2">{s.name}</h3>
                        <p className="text-sm text-muted-foreground mb-4">{s.desc}</p>
                        <div className="font-serif text-3xl">{s.price}€</div>
                    </button>
                ))}
            </div>

            {selected && (
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-3xl border border-border/60 bg-card p-8"
                    data-testid="gift-form"
                >
                    <h2 className="font-serif text-2xl mb-6">À qui offrez-vous ?</h2>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                            <Label className="mb-2 block">Nom du destinataire</Label>
                            <Input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} className="h-12 rounded-full px-5" data-testid="gift-recipient-name" />
                        </div>
                        <div>
                            <Label className="mb-2 block">Email du destinataire</Label>
                            <Input type="email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} className="h-12 rounded-full px-5" data-testid="gift-recipient-email" />
                        </div>
                    </div>
                    <div className="mb-4">
                        <Label className="mb-2 block">Votre nom</Label>
                        <Input value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} className="h-12 rounded-full px-5" data-testid="gift-sender-name" />
                    </div>
                    <div className="mb-6">
                        <Label className="mb-2 block">Message personnel (optionnel)</Label>
                        <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-2xl" placeholder="Un mot doux qui accompagnera ce cadeau…" data-testid="gift-message" />
                    </div>
                    <Button onClick={openPayment} disabled={busy} className="w-full rounded-full h-12 bg-primary text-primary-foreground hover:scale-[1.02] transition-transform" data-testid="gift-submit">
                        {busy ? "Redirection…" : "Payer et offrir"} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                </motion.div>
            )}
            <PaymentMethodDialog
                open={!!dialog}
                onOpenChange={(o) => !o && setDialog(null)}
                item={dialog}
                stripeCheckout={stripeCheckout}
            />
        </div>
    );
}
